 import { useState, useEffect } from 'react';
 import { useParams } from 'react-router-dom';
 import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { Info, AlertCircle, Loader2 } from 'lucide-react';
import { useProducerById, useUpdateProducer } from '@/hooks/useMyProducers';
 import { DEMO_FARM } from '@/hooks/useMyFarms';
import { toast } from '@/hooks/use-toast';
import {
  BasicInfoSection,
  useProducerEditorState,
} from '@/features/producer-editor';
import { ProducerSectionHeader } from '@/components/producer/ProducerSectionHeader';
import { useProducerFormData } from '@/hooks/useProducerFormData';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { validateSlug } from '@/lib/slug';

export default function ProducerGrundinfo() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading, error, refetch } = useProducerById(farmId);
  const updateProducerMutation = useUpdateProducer();
  const [showLongDescription, setShowLongDescription] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const { isReadOnly, hasCapability } = useProducerAccess(farmId !== 'demo' ? farmId : undefined);

  const isDemo = farmId === 'demo';
  const isFallbackMode = isDemo || (!producer && !isLoading);
  const canEdit = isDemo || hasCapability('canEditProducerInfo');

   const { initialFormData } = useProducerFormData(
    producer,
    isDemo,
    farmId
  );

  // Editor state hook
  const editorState = useProducerEditorState({
    initialData: initialFormData,
    onSave: async (data) => {
      if (isDemo) {
        toast({ title: 'Sparat', description: 'Sparat (demo).' });
        return;
      }
      if (!producer) return;

      // Validate slug if provided
      if (data.slug) {
        const slugErr = validateSlug(data.slug);
        if (slugErr) {
          setSlugError(slugErr);
          throw new Error(slugErr);
        }
      }

      const payload: Record<string, any> = {
        name: data.name,
        short_description: data.shortDescription,
        description: data.longDescription || null,
      };

      // Only include slug if it changed
      if (data.slug !== (producer.slug || '')) {
        payload.slug = data.slug || null;
      }

      try {
        await updateProducerMutation.mutateAsync({
          id: producer.id,
          updates: payload,
        });
        setSlugError(null);
        toast({ title: 'Sparat', description: 'Dina ändringar har sparats.' });
      } catch (err: any) {
        // Handle unique constraint violation on slug
        const msg = err?.message || '';
        if (msg.includes('producers_slug_key') || msg.includes('duplicate key') || msg.includes('unique')) {
          setSlugError('Den här adressen används redan av en annan gård.');
          throw err;
        }
        throw err;
      }
    },
  });

  useEffect(() => {
    if (editorState.formData.longDescription) {
      setShowLongDescription(true);
    }
  }, [editorState.formData.longDescription]);

  if (isLoading && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Laddar..." title="Grundinfo">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (error && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Fel" title="Grundinfo">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Kunde inte ladda gården.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Försök igen
        </Button>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout
      farmId={farmId || 'demo'}
      producerName={editorState.formData.name || 'Din gård'}
      title="Grundinfo"
    >
      <ProducerSectionHeader
        title="Grundinfo"
        description=""
        onSave={canEdit ? editorState.save : undefined}
        isSaving={editorState.isSaving || updateProducerMutation.isPending}
        isDirty={editorState.isDirty}
        isReadOnly={!canEdit}
      />

      {isFallbackMode && (
        <Alert className="border-blue-200 bg-blue-50 mb-6">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Demo-läge:</strong> Du tittar på en förhandsgranskning.
          </AlertDescription>
        </Alert>
      )}

      <BasicInfoSection
        formData={editorState.formData}
        errors={editorState.errors}
        updateField={editorState.updateField}
        showLongDescription={showLongDescription}
        onToggleLongDescription={setShowLongDescription}
        producerId={producer?.id}
        currentSlug={producer?.slug ?? null}
        slugError={slugError}
        onSlugErrorClear={() => setSlugError(null)}
      />
    </ProducerLayout>
  );
}
